import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Link from 'next/link';
import ProductGrid from '../components/product/ProductGrid';
import ProductFilters from '../components/product/ProductFilters';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import AnimatedDiv from '../components/ui/AnimatedDiv';

const ProductsPage = () => {
 const router = useRouter();
 const [products, setProducts] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const [isSearchMode, setIsSearchMode] = useState(false);
 const [searchTerm, setSearchTerm] = useState('');
 const [filters, setFilters] = useState({
 category: '',
 minPrice: '',
 maxPrice: '',
 search: '',
 sortBy: 'name',
 sortOrder: 'asc'
 });
 const [currentPage, setCurrentPage] = useState(1);
 const [pagination, setPagination] = useState(null);
 const productsPerPage = 12;
 const isInitialized = useRef(false);
 const isUserAction = useRef(false);

 // Sync page from URL (handles both initial load and back/forward navigation)
 useEffect(() => {
 if (router.isReady) {
 const { search, page } = router.query;

 // Handle search param
 if (search && search !== filters.search) {
 setIsSearchMode(true);
 setSearchTerm(search);
 setFilters(prev => ({ ...prev, search }));
 } else if (!search && filters.search && isInitialized.current) {
 setIsSearchMode(false);
 setSearchTerm('');
 setFilters(prev => ({ ...prev, search: '' }));
 }

 // Sync page from URL (for back/forward navigation)
 const urlPage = page ? parseInt(page, 10) : 1;
 if (!isNaN(urlPage) && urlPage > 0 && urlPage !== currentPage && !isUserAction.current) {
 setCurrentPage(urlPage);
 }
 isUserAction.current = false;
 isInitialized.current = true;
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [router.isReady, router.query]);

 // Fetch products when filters or page change (but only after initialization)
 useEffect(() => {
 if (isInitialized.current) {
 fetchProducts();
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [filters, currentPage]);

 const fetchProducts = useCallback(async () => {
 try {
 setLoading(true);
 setError(null);

 // Build query parameters
 const queryParams = new URLSearchParams();
 Object.entries(filters).forEach(([key, value]) => {
 if (value && value !== '') {
 queryParams.append(key, value);
 }
 });

 // Add pagination parameters
 queryParams.append('page', currentPage);
 queryParams.append('limit', productsPerPage);

 const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'https://us-central1-fenparet-website.cloudfunctions.net/api';
 const response = await fetch(`${apiUrl}/products?${queryParams}`, {
 headers: {
 'Content-Type': 'application/json',
 },
 mode: 'cors',
 credentials: 'include',
 });

 if (!response.ok) {
 if (response.status === 429) {
 throw new Error('Trop de requêtes. Veuillez patienter et réessayer.');
 } else if (response.status >= 500) {
 throw new Error('Erreur du serveur. Veuillez réessayer plus tard.');
 } else {
 throw new Error(`Erreur ${response.status}: ${response.statusText}`);
 }
 }

 const data = await response.json();
 setProducts(data.products || []);
 setPagination(data.pagination || null);
 } catch (err) {
 console.error('Error fetching products:', err);
 setProducts([]);
 setPagination(null);

 // Set specific error message based on error type
 if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
 setError('Problème de connexion réseau. Vérifiez votre connexion internet.');
 } else if (err.message.includes('429')) {
 setError('Trop de requêtes. Veuillez patienter et réessayer.');
 } else if (err.message.includes('500')) {
 setError('Erreur du serveur. Veuillez réessayer plus tard.');
 } else {
 setError(err.message || 'Erreur lors du chargement des produits.');
 }
 } finally {
 setLoading(false);
 }
 }, [filters, currentPage, productsPerPage]);

 const handleFilterChange = (newFilters) => {
 isUserAction.current = true;
 setFilters(prev => ({ ...prev, ...newFilters }));
 // Reset to page 1 when filters change
 setCurrentPage(1);
 // Remove page param from URL
 if (router.query.page) {
 const query = { ...router.query };
 delete query.page;
 router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
 }
 };

 const handlePageChange = (newPage) => {
 isUserAction.current = true;
 setCurrentPage(newPage);
 // Update URL with the new page number (shallow routing to avoid full page reload)
 const query = { ...router.query, page: newPage };
 if (newPage === 1) {
 delete query.page; // Remove page param when on page 1 for cleaner URLs
 }
 router.push({ pathname: router.pathname, query }, undefined, { shallow: true });
 window.scrollTo({ top: 0, behavior: 'smooth' });
 };

 const handleNextPage = () => {
 if (pagination?.hasNext) {
 handlePageChange(currentPage + 1);
 }
 };

 const handlePrevPage = () => {
 if (pagination?.hasPrev) {
 handlePageChange(currentPage - 1);
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen flex items-center justify-center">
 <LoadingSpinner />
 </div>
 );
 }

 if (error) {
 return (
 <div className="container mx-auto px-4 py-8">
 <div className="text-center">
 <h1 className="text-2xl font-bold text-error mb-4">Erreur de connexion</h1>
 <p className="text-theme-secondary mb-4">Impossible de charger les produits. Vérifiez votre connexion internet.</p>
 <p className="text-sm text-theme-tertiary mb-4">Détails: {error}</p>
 <button
 onClick={fetchProducts}
 className="btn-primary px-4 py-2"
 >
 Réessayer
 </button>
 </div>
 </div>
 );
 }

 return (
 <>
 <Head>
 <title>
 {isSearchMode 
 ? `Recherche: &quot;${searchTerm}&quot; - ${process.env.NEXT_PUBLIC_SITE_NAME}`
 : `Produits - ${process.env.NEXT_PUBLIC_SITE_NAME}`
 }
 </title>
 <meta name="description" content={
 isSearchMode 
 ? `Résultats de recherche pour &quot;${searchTerm}&quot; - ${products.length} produits trouvés`
 : "Parcourez notre collection complète de produits"
 } />
 </Head>
 
 <div className="container mx-auto px-4 py-8">
 <h1 className="sr-only">Produits</h1>
 <AnimatedDiv animation="slideInFromLeft" className="space-y-8">
 {/* Products Section - Now on top */}
 <div>
 {/* Search Results Header */}
 {isSearchMode && (
 <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
 <h2 className="text-lg font-semibold text-gray-900 mb-1">
 Résultats de recherche pour &quot;{searchTerm}&quot;
 </h2>
 <p className="text-gray-600 text-sm">
 {products.length} produit{products.length !== 1 ? 's' : ''} trouvé{products.length !== 1 ? 's' : ''}
 </p>
 </div>
 )}
 
 {/* Regular Results Summary */}
 {!isSearchMode && (
 <div className="mb-6 flex justify-between items-center">
 <p className="text-theme-secondary">
 {pagination ? pagination.total : products.length} produits trouvés
 </p>
 
 {/* Sort Options */}
 <div className="flex items-center space-x-4">
 <label className="text-sm text-theme-secondary">
 Trier par:
 </label>
 <select 
 value={`${filters.sortBy}-${filters.sortOrder}`}
 onChange={(e) => {
 const [sortBy, sortOrder] = e.target.value.split('-');
 handleFilterChange({ sortBy, sortOrder });
 }}
 className="border border-theme-border rounded px-3 py-1 bg-theme-input text-theme-primary focus:outline-none focus:ring-2 focus:ring-accent"
 >
 <option value="name-asc">Nom A-Z</option>
 <option value="name-desc">Nom Z-A</option>
 <option value="price-asc">Prix croissant</option>
 <option value="price-desc">Prix décroissant</option>
 <option value="createdAt-desc">Plus récents</option>
 </select>
 </div>
 </div>
 )}

 {/* Search Mode Sort Options */}
 {isSearchMode && (
 <div className="mb-6 flex justify-end items-center">
 <div className="flex items-center space-x-4">
 <label className="text-sm text-gray-600">
 Trier par:
 </label>
 <select 
 value={`${filters.sortBy}-${filters.sortOrder}`}
 onChange={(e) => {
 const [sortBy, sortOrder] = e.target.value.split('-');
 handleFilterChange({ sortBy, sortOrder });
 }}
 className="border border-gray-300 rounded px-3 py-1 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
 >
 <option value="name-asc">Nom A-Z</option>
 <option value="name-desc">Nom Z-A</option>
 <option value="price-asc">Prix croissant</option>
 <option value="price-desc">Prix décroissant</option>
 <option value="createdAt-desc">Plus récents</option>
 </select>
 </div>
 </div>
 )}

 {/* Products Grid */}
 <ProductGrid products={products} />

 {/* Pagination Controls */}
 {pagination && pagination.pages > 1 && products.length > 0 && (
 <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-theme-border pt-6">
 <div className="text-sm text-theme-secondary">
 Affichage de {((currentPage - 1) * productsPerPage) + 1} à {Math.min(currentPage * productsPerPage, pagination.total)} sur {pagination.total} produits
 </div>

 <div className="flex items-center gap-2">
 {/* Previous Button */}
 <button
 onClick={handlePrevPage}
 disabled={!pagination.hasPrev}
 className={`px-4 py-2 rounded-lg font-medium transition-colors ${
 pagination.hasPrev
 ? 'bg-theme-surface text-theme-primary hover:bg-accent hover:text-white border border-theme-border'
 : 'bg-gray-100 text-gray-400 cursor-not-allowed'
 }`}
 >
 Précédent
 </button>

 {/* Page Numbers */}
 <div className="flex items-center gap-1">
 {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
 let pageNum;
 if (pagination.pages <= 5) {
 pageNum = i + 1;
 } else if (currentPage <= 3) {
 pageNum = i + 1;
 } else if (currentPage >= pagination.pages - 2) {
 pageNum = pagination.pages - 4 + i;
 } else {
 pageNum = currentPage - 2 + i;
 }

 return (
 <button
 key={pageNum}
 onClick={() => handlePageChange(pageNum)}
 className={`w-10 h-10 rounded-lg font-medium transition-colors ${
 currentPage === pageNum
 ? 'bg-accent text-white'
 : 'bg-theme-surface text-theme-primary hover:bg-accent hover:text-white border border-theme-border'
 }`}
 >
 {pageNum}
 </button>
 );
 })}
 </div>

 {/* Next Button */}
 <button
 onClick={handleNextPage}
 disabled={!pagination.hasNext}
 className={`px-4 py-2 rounded-lg font-medium transition-colors ${
 pagination.hasNext
 ? 'bg-theme-surface text-theme-primary hover:bg-accent hover:text-white border border-theme-border'
 : 'bg-gray-100 text-gray-400 cursor-not-allowed'
 }`}
 >
 Suivant
 </button>
 </div>
 </div>
 )}

 {products.length === 0 && !loading && (
 <div className="text-center py-12">
 <div className="text-6xl mb-4">
 {isSearchMode ? '' : ''}
 </div>
 <h3 className="text-xl font-semibold text-gray-900 mb-2">
 {isSearchMode 
 ? `Aucun produit trouvé pour &quot;${searchTerm}&quot;`
 : 'Aucun produit trouvé'
 }
 </h3>
 <p className="text-gray-600 mb-4">
 {isSearchMode 
 ? 'Aucun produit ne correspond à votre recherche. Essayez avec un autre terme de recherche.'
 : 'Essayez d\'ajuster vos filtres ou termes de recherche'
 }
 </p>
 {isSearchMode ? (
 <div className="space-y-3">
 <Link 
 href="/products"
 className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors"
 >
 Voir tout notre inventaire
 </Link>
 <p className="text-sm text-gray-500">
 ou essayez une recherche avec un autre terme
 </p>
 </div>
 ) : (
 <button 
 onClick={() => setFilters({
 category: '',
 minPrice: '',
 maxPrice: '',
 search: '',
 sortBy: 'name',
 sortOrder: 'asc'
 })}
 className="btn-primary"
 >
 Effacer les filtres
 </button>
 )}
 </div>
 )}
 </div>

 {/* Filters Section - Now below products */}
 <div>
 <h2 className="text-xl font-bold text-theme-primary mb-4">Filtrer les produits</h2>
 <ProductFilters 
 filters={filters} 
 onFilterChange={handleFilterChange}
 />
 </div>
 </AnimatedDiv>
 </div>
 </>
 );
};

export default ProductsPage;